import {
  useCallback, useEffect, useState,
} from 'react';
import { useDispatch } from 'react-redux';
import {
  ghostOutlineHoverVisuals,
  ghostOutlineVisuals,
  outlineHoverVisuals, outlineVisuals,
} from '~/_shared/constants/mapObjects/mapObjectOutline/outlineVisuals.constants';
import { type LatLng } from '~/_shared/types/latLng';
import { MarkerColor } from '~/_shared/types/marker.types';
import { type Polygon } from '~/_shared/types/polygon/polygon.types';
import { type MapObjectOutlineInstance } from '~/map/map/mapObjects/mapOutline/mapOutlineModel';
import { useLassoToolPolygonZIndex as useLassoToolPolygonInstanceZIndex } from '~/map/zIndexes/useMapPolygonInstanceZIndex.hook';
import {
  hoverLassoOutline,
  hoverLassoRemoveButton,
  startLassoDrag,
  startLassoPolygonHover,
  stopLassoDrag,
  stopLassoOutlineHover,
  stopLassoPolygonHover,
  stopLassoRemoveButtonHover,
} from '~/store/frontendState/mapTools/lasso/lasso.actionCreators';
import { MapOutline } from '../../mapObjects/mapOutline/mapOutline.component';
import {
  DrawMapShape, DrawMapShapeMode,
} from '../../mapObjects/mapShape/drawMapShape.container';
import {
  EditMapShapeContainer, type OutlineCallbackProps,
} from '../../mapObjects/mapShape/editMapShape.container';
import {
  MapShapeLine, type MapShapePolylineVisualsConfig,
} from '../../mapObjects/mapShape/mapShapeLine.component';
import {
  type MapShapeInstance, shapeOutlinesToPath,
} from '../../mapObjects/mapShape/mapShapeModel';
import {
  MapShapePolygon, type MapShapePolygonVisualConfig,
} from '../../mapObjects/mapShape/mapShapePolygon.component';
import {
  MapShapeRemoveButton, type MapShapeRemoveButtonVisualsConfig,
} from '../../mapObjects/mapShape/mapShapeRemoveButton.component';

const lineVisuals: MapShapePolylineVisualsConfig = {
  color: [88, 125, 215, 1],
  style: 'solid',
  width: 3,
};

export const removeButtonVisuals: MapShapeRemoveButtonVisualsConfig = {
  color: MarkerColor.Black,
};

export const removeButtonHoverVisuals: MapShapeRemoveButtonVisualsConfig = {
  color: MarkerColor.Red,
};

export const polygonVisuals: MapShapePolygonVisualConfig = {
  fillColor: [0, 0, 255, 0.2],
};

export type LassoToolDrawProps = {
  readonly isDrawing: boolean;

  readonly onDrawingFinished: (polygon?: Polygon) => void;
  readonly onPolygonClick: (polygon?: Polygon) => void;
  readonly onRemove: () => void;
};

export const LassoToolDrawComponent: React.FC<LassoToolDrawProps> = ({ onPolygonClick, onDrawingFinished, isDrawing, onRemove }) => {
  const dispatch = useDispatch();

  const [hoveredOutlineId, setHoveredOutlineId] = useState<null | Uuid>(null);
  const [removeHovered, setRemoveHovered] = useState(false);
  const [shape, setShape] = useState<MapShapeInstance | undefined>();

  const zIndex = useLassoToolPolygonInstanceZIndex(shape?.id);

  const onShapeChange = useCallback((shape: MapShapeInstance) => {
    setShape(shape);
  }, []);

  const onPolygonMouseOver = useCallback(() => {
    dispatch(startLassoPolygonHover());
  }, [dispatch]);

  const onPolygonMouseOut = useCallback(() => {
    dispatch(stopLassoPolygonHover());
  }, [dispatch]);

  const onPolygonClickCallback = useCallback(() => {
    if (!shape) {
      onPolygonClick();
      return;
    }
    onPolygonClick({ path: shapeOutlinesToPath(shape.outlines), holes: [] });
  }, [shape, onPolygonClick]);

  const onRemoveMouseOver = useCallback(() => {
    setRemoveHovered(true);
    dispatch(hoverLassoRemoveButton());
  }, [dispatch]);

  const onRemoveMouseOut = useCallback(() => {
    setRemoveHovered(false);
    dispatch(stopLassoRemoveButtonHover());
  }, [dispatch]);

  const renderLine = useCallback((id: string, start: LatLng, end: LatLng) => (
    <MapShapeLine
      key={id}
      id={id}
      start={start}
      end={end}
      visuals={lineVisuals}
    />
  ), []);

  const renderPolygon = useCallback(() => (
    <>
      <MapShapePolygon
        visuals={polygonVisuals}
        onClick={onPolygonClickCallback}
        onMouseOut={onPolygonMouseOut}
        onMouseOver={onPolygonMouseOver}
      />
      <MapShapeRemoveButton
        onClick={onRemove}
        onMouseOver={onRemoveMouseOver}
        onMouseOut={onRemoveMouseOut}
        visuals={removeHovered ? removeButtonHoverVisuals : removeButtonVisuals}
      />
    </>
  ), [onPolygonClickCallback, onPolygonMouseOut, onPolygonMouseOver, onRemove, onRemoveMouseOut, onRemoveMouseOver, removeHovered]);

  const onOutlineMouseOver = useCallback((outlineId: Uuid) => {
    if (isDrawing && !isDrawingOutlineHoverEnabled(shape, outlineId)) {
      return;
    }

    setHoveredOutlineId(outlineId);
    dispatch(hoverLassoOutline());
  }, [dispatch, isDrawing, shape]);

  const onOutlineMouseOut = useCallback(() => {
    setHoveredOutlineId(null);
    dispatch(stopLassoOutlineHover());
  }, [dispatch]);

  const onOutlineDragStart = useCallback(() => {
    dispatch(startLassoDrag());
  }, [dispatch]);

  const onOutlineDragEnd = useCallback(() => {
    dispatch(stopLassoDrag());
  }, [dispatch]);

  const renderDrawingOutline = useCallback((id: string, outline: MapObjectOutlineInstance) => {
    return (
      <MapOutline
        key={id}
        outline={outline}
        visuals={hoveredOutlineId === outline.id ? outlineHoverVisuals : outlineVisuals}
        onMouseOver={onOutlineMouseOver}
        onMouseOut={onOutlineMouseOut}
      />
    );
  }, [onOutlineMouseOut, onOutlineMouseOver, hoveredOutlineId]);

  const renderOutline = useCallback((
    id: string, outline: MapObjectOutlineInstance, isGhost: boolean, callbacks: OutlineCallbackProps
  ) => {
    const visuals = isGhost
      ? (hoveredOutlineId === id ? ghostOutlineHoverVisuals : ghostOutlineVisuals)
      : (hoveredOutlineId === id ? outlineHoverVisuals : outlineVisuals);

    return (
      <MapOutline
        key={id}
        outline={outline}
        visuals={visuals}
        onDragStart={() => {
          callbacks.onDragStart();
          onOutlineDragStart();
        }}
        onDragMove={(_, latLng) => callbacks.onDragMove(latLng)}
        onDragEnd={() => {
          callbacks.onDragEnd();
          onOutlineDragEnd();
        }}
        onMouseOver={(outlineId) => {
          callbacks.onOutlineMouseOver();
          onOutlineMouseOver(outlineId);
        }}
        onMouseOut={onOutlineMouseOut}
      />
    );
  }, [hoveredOutlineId, onOutlineMouseOver, onOutlineMouseOut, onOutlineDragStart, onOutlineDragEnd]);

  const onDrawingDone = useCallback(() => {
    if (!shape || shape.outlines.length < 3) {
      onRemove();
      return;
    }

    onDrawingFinished({ path: shapeOutlinesToPath(shape.outlines), holes: [] });
  }, [onDrawingFinished, onRemove, shape]);

  useEffect(() => {
    setHoveredOutlineId(null);
  }, [isDrawing]);

  if (!isDrawing && shape && shape.outlines.length > 2) {
    return (
      <EditMapShapeContainer
        key={shape.id}
        isPolygon
        initialMapShape={shape}
        zIndex={zIndex}
        onChange={onShapeChange}
        renderOutline={renderOutline}
        renderLine={renderLine}
        renderPolygon={renderPolygon}
      />
    );
  }

  if (isDrawing) {
    return (
      <DrawMapShape
        zIndex={zIndex}
        onDone={onDrawingDone}
        onChange={onShapeChange}
        renderOutline={renderDrawingOutline}
        renderLine={renderLine}
        mode={DrawMapShapeMode.Freeform}
      />
    );
  }

  return null;
};

const isFirstOutline = (shape: MapShapeInstance | undefined, outlineId: string) =>
  shape?.outlines[0]?.id === outlineId;

const isDrawingOutlineHoverEnabled = (shape: MapShapeInstance | undefined, outlineId: string) => {
  const isFirst = isFirstOutline(shape, outlineId);
  return isFirst && !!(shape && shape.outlines.length > 2);
};
